import React, { useState } from "react";
import { Container, Row, Col, Card, Modal, Button } from "react-bootstrap";
import { motion } from "framer-motion";
import { FaCode, FaLaptopCode, FaPaintBrush } from "react-icons/fa";
import './Services.css';

const services = [
  {
    name: "Web Development",
    icon: <FaCode size={50} style={{ color: "#007bff" }} />,
    description: "Building fast, responsive, and modern websites using the latest technologies.",
    details: (
      <>
        <p>
          Whether you're an individual or business, I provide end-to-end web development services — from concept to full deployment — using modern, scalable technologies.
        </p>
        <h6 className="fw-bold mt-3">What’s Included:</h6>
        <ul>
          <li>Responsive website development from scratch</li>
          <li>Static, dynamic, and single-page applications</li>
          <li>Modern frontend: HTML, CSS, JavaScript, React, Next.js</li>
          <li>Backend: Node.js, Express.js, MongoDB or SQL</li>
          <li>REST API development and integration</li>
          <li>Performance optimization & SEO-friendly structure</li>
          <li>Cross-browser and mobile-first design</li>
          <li>Version control using Git and GitHub</li>
        </ul>
        <h6 className="fw-bold mt-3">Tools & Technologies:</h6>
        <ul>
          <li>React, Next.js, Tailwind, Bootstrap</li>
          <li>Node.js, Express.js, MongoDB, PostgreSQL</li>
          <li>Git, GitHub, Postman, Vercel, Netlify</li>
        </ul>
        <h6 className="fw-bold mt-3">Pricing & Timeline:</h6>
        <ul>
          <li><strong>Rate:</strong> $8/hour</li>
          <li><strong>Typical delivery:</strong> 3–7 days (based on project scope)</li>
        </ul>
        <h6 className="fw-bold mt-3">💳 Payment Milestones:</h6>
        <ul>
          <li><strong>30%</strong> upfront payment (project confirmation)</li>
          <li><strong>40%</strong> after mid-project demo & review</li>
          <li><strong>30%</strong> upon final delivery & deployment</li>
        </ul>
        <p className="mt-3 mb-0">
          Secure payments via PayPal, UPI, or bank transfer. Invoice provided.
        </p>
      </>
    )
  },
  {
    name: "Software Development",
    icon: <FaLaptopCode size={50} style={{ color: "#28a745" }} />,
    description: "Designing and developing scalable software solutions tailored to client needs.",
    details: (
      <>
        <p>
          I build robust, scalable software applications using best practices and clean architecture.
        </p>
        <h6 className="fw-bold mt-3">What’s Included:</h6>
        <ul>
          <li>Backend development with Java, Python, or Node.js</li>
          <li>REST API creation and third-party integration</li>
          <li>Database design: SQL & NoSQL (MySQL, PostgreSQL, MongoDB)</li>
          <li>Authentication, error handling, and deployment setup</li>
        </ul>
        <h6 className="fw-bold mt-3">Tools & Technologies:</h6>
        <ul>
          <li>Java, Python, Node.js, Express.js</li>
          <li>MongoDB, MySQL, PostgreSQL</li>
          <li>Git, GitHub, Postman, Docker (if needed)</li>
        </ul>
        <h6 className="fw-bold mt-3">Pricing & Timeline:</h6>
        <ul>
          <li><strong>Rate:</strong> $8/hour</li>
          <li><strong>Delivery:</strong> 5–10 days depending on scope</li>
        </ul>
        <h6 className="fw-bold mt-3">💳 Payment Milestones:</h6>
        <ul>
          <li><strong>30%</strong> upfront payment</li>
          <li><strong>40%</strong> after mid-stage testing</li>
          <li><strong>30%</strong> after final delivery</li>
        </ul>
        <p className="mt-3 mb-0">
          Payments accepted via UPI, PayPal, or bank. Documentation and invoice available.
        </p>
      </>
    )
  },
  {
    name: "Portfolio Website",
    icon: <FaPaintBrush size={50} style={{ color: "#ffc107" }} />,
    description: "Creating personalized and visually engaging portfolio websites.",
    details: (
      <>
        <p>
          I design and develop custom portfolio websites that reflect your personality, skills, and brand.
        </p>
        <h6 className="fw-bold mt-3">What’s Included:</h6>
        <ul>
          <li>Modern design using Figma or custom mockups</li>
          <li>Fully responsive, mobile-first UI</li>
          <li>Technologies: HTML, CSS, JavaScript, React, Tailwind</li>
          <li>SEO-friendly and optimized for performance</li>
        </ul>
        <h6 className="fw-bold mt-3">Tools & Technologies:</h6>
        <ul>
          <li>React, Tailwind, Bootstrap</li>
          <li>Figma to code conversion</li>
          <li>Hosting via Netlify, GitHub Pages, or Vercel</li>
        </ul>
        <h6 className="fw-bold mt-3">Pricing & Timeline:</h6>
        <ul>
          <li><strong>Rate:</strong> $8/hour</li>
          <li><strong>Delivery:</strong> 2–5 days depending on content</li>
        </ul>
        <h6 className="fw-bold mt-3">💳 Payment Milestones:</h6>
        <ul>
          <li><strong>30%</strong> advance at project start</li>
          <li><strong>40%</strong> after design preview/demo</li>
          <li><strong>30%</strong> at final deployment</li>
        </ul>
        <p className="mt-3 mb-0">
          Pay via UPI, PayPal, or bank transfer. Transparent billing with invoices.
        </p>
      </>
    )
  }
];

const Services = () => {
  const [showModal, setShowModal] = useState(false);
  const [currentService, setCurrentService] = useState(null);

  const handleShowModal = (service) => {
    setCurrentService(service);
    setShowModal(true);
  };

  const handleCloseModal = () => setShowModal(false);

  return (
    <motion.section
      id="services"
      className="services bg-light text-dark py-5"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
    >
      <Container>
        <h2 className="text-center mb-5 fw-bold display-5">Our Services</h2>
        <Row className="g-4">
          {services.map((service, index) => (
            <Col key={index} md={4} sm={6}>
              <motion.div whileHover={{ scale: 1.05 }} transition={{ duration: 0.3 }}>
                <Card className="shadow-sm border-0 h-100 service-card text-center">
                  <Card.Body className="p-4 d-flex flex-column justify-content-between">
                    <div>
                      <div className="mb-3">{service.icon}</div>
                      <h4 className="fw-semibold mb-3">{service.name}</h4>
                      <p className="text-muted">{service.description}</p>
                    </div>
                    <Button
                      variant="primary"
                      className="mt-3 fw-semibold"
                      onClick={() => handleShowModal(service)}
                    >
                      View Details
                    </Button>
                  </Card.Body>
                </Card>
              </motion.div>
            </Col>
          ))}
        </Row>
      </Container>

      {currentService && (
        <Modal show={showModal} onHide={handleCloseModal} centered>
          <Modal.Header closeButton className="border-0">
            <Modal.Title className="fw-semibold">{currentService.name}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="fs-6 text-secondary">
            <h5 className="fw-semibold mb-3">About the Service</h5>
            {currentService.details}
          </Modal.Body>
          <Modal.Footer className="border-0">
            <Button variant="outline-secondary" onClick={handleCloseModal}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </motion.section>
  );
};

export default Services;
